import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { fetchCountriesBulk } from '../../utils/countries';
import { formatNumber } from '../../utils/formatters';
import SteamAvatar from '../common/SteamAvatar';
import SteamFlag from '../common/SteamFlag';
import './Leaderboard.css';

/**
 * Demo-derived all-time stat leaderboards. Unlike the per-stat MatchZy
 * <Leaderboard> cards, this fetches the Battle Bot API's aggregated
 * /api/stats/leaderboards payload once and renders one card per category,
 * grouped into themed sections.
 *
 * Only categories distinct from the existing MatchZy cards are shown here
 * (so the Topplistor page does not duplicate K/D, kills, HS%, ADR). Cards whose
 * backend list is empty (e.g. a board that needs a not-yet-run demo backfill)
 * are hidden automatically.
 */

// Themed groups of cards. Each card: { key (backend category), title, icon,
// pct? (render value as a percentage) }. Keys must match the `categories`
// payload from /api/stats/leaderboards.
const SECTIONS = [
  {
    title: 'Fragging',
    cards: [
      { key: 'aces', title: 'Aces (5K)', icon: '🏅' },
      { key: 'multikills', title: 'Multi-kills (3K+)', icon: '🔫' },
      { key: 'awp_kills', title: 'AWP Kills', icon: '🎯' },
      { key: 'knife_kills', title: 'Knife Kills', icon: '🔪' },
    ],
  },
  {
    title: 'Trickshots',
    cards: [
      { key: 'noscope_kills', title: 'No-scope Kills', icon: '🔭' },
      { key: 'wallbang_kills', title: 'Wallbang Kills', icon: '🧱' },
      { key: 'smoke_kills', title: 'Through-smoke Kills', icon: '🌫️' },
      { key: 'blind_kills', title: 'Blind Kills', icon: '😎' },
    ],
  },
  {
    title: 'Clutch & Duels',
    cards: [
      { key: 'clutch_rate', title: 'Clutch Win%', icon: '🧊', pct: true },
      { key: 'opening_duel', title: 'Opening-duel Win%', icon: '⚔️', pct: true },
      { key: 'trade_kills', title: 'Trade Kills', icon: '🔁' },
    ],
  },
  {
    title: 'Support & Utility',
    cards: [
      { key: 'utility_damage', title: 'Utility Damage', icon: '💥' },
      { key: 'molotov_damage', title: 'Molotov Damage', icon: '🔥' },
      { key: 'smokes_thrown', title: 'Smokes Thrown', icon: '💨' },
      { key: 'decoys_thrown', title: 'Decoys Thrown', icon: '🤡' },
      { key: 'assists', title: 'Assists', icon: '🤝' },
      { key: 'kast', title: 'KAST %', icon: '📊', pct: true },
    ],
  },
  {
    title: 'Bomb & Objective',
    cards: [
      { key: 'bomb_plants', title: 'Bomb Plants', icon: '💣' },
      { key: 'bomb_defuses', title: 'Bomb Defuses', icon: '✂️' },
    ],
  },
  {
    title: 'Economy & Rounds',
    cards: [
      { key: 'eco_win_rate', title: 'Eco Win%', icon: '🪙', pct: true },
      { key: 'pistol_win_rate', title: 'Pistol Win%', icon: '🔫', pct: true },
      { key: 'post_plant_rate', title: 'Post-plant Win%', icon: '🧨', pct: true },
      { key: 'retake_rate', title: 'Retake Win%', icon: '🛡️', pct: true },
    ],
  },
  {
    title: 'Movement',
    cards: [
      { key: 'distance_km', title: 'Distance Travelled', icon: '🏃', unit: ' km' },
    ],
  },
  {
    title: 'Hall of Shame',
    cards: [
      { key: 'opening_deaths', title: 'First to Die', icon: '⚰️' },
      { key: 'team_flashes', title: 'Team Flashes', icon: '🙈' },
      { key: 'team_kills', title: 'Team Kills', icon: '😱' },
      { key: 'team_damage', title: 'Team Damage', icon: '🩸' },
      { key: 'self_flashes', title: 'Self Flashes', icon: '🤦' },
    ],
  },
];

/**
 * Renders the demo-derived stat leaderboards, grouped into themed sections.
 *
 * @param {Object} props
 * @param {(steamId: string) => void} props.onSelectPlayer - Called when a row is
 *   clicked, with the player's steamid64.
 * @returns {JSX.Element|null} The leaderboards sections, or null before data loads.
 */
export default function StatLeaderboards({ onSelectPlayer }) {
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/leaderboards?limit=5`)
      .then(res => res.json())
      .then(async (data) => {
        const cats = data.categories || {};
        const ids = new Set();
        Object.values(cats).forEach(list => (list || []).forEach(e => ids.add(e.steam_id)));
        await Promise.all([fetchAvatarsBulk([...ids]), fetchCountriesBulk([...ids])]);
        setCategories(cats);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const formatValue = (value, card) => {
    if (card.pct) return `${value}%`;
    if (card.unit) return `${formatNumber(value)}${card.unit}`;
    return formatNumber(value);
  };

  if (loading) {
    const skeletonCards = SECTIONS.flatMap(s => s.cards).slice(0, 8);
    return (
      <div className="leaderboards-grid">
        {skeletonCards.map(c => (
          <div key={c.key} className="leaderboard-card">
            <div className="leaderboard-header">
              <span className="leaderboard-icon">{c.icon}</span>
              <h3>{c.title}</h3>
            </div>
            <div className="loading-skeleton">
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton-row" />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!categories) return null;

  /**
   * Renders a single leaderboard card, or null when its category has no entries.
   *
   * @param {{key: string, title: string, icon: string, pct?: boolean}} card
   * @returns {JSX.Element|null}
   */
  const renderCard = (card) => {
    const entries = categories[card.key] || [];
    if (entries.length === 0) return null;
    return (
      <div key={card.key} className="leaderboard-card">
        <div className="leaderboard-header">
          <span className="leaderboard-icon">{card.icon}</span>
          <h3>{card.title}</h3>
        </div>
        <div className="leaderboard-list">
          {entries.map((entry, index) => (
            <div
              key={entry.steam_id}
              className={`leaderboard-row clickable ${index === 0 ? 'top-player' : ''}`}
              onClick={() => onSelectPlayer && onSelectPlayer(entry.steam_id)}
            >
              <span className={`rank rank-${index + 1}`}>{index + 1}</span>
              <SteamAvatar steamid64={entry.steam_id} name={entry.player_name} size="small" />
              <SteamFlag steamid64={entry.steam_id} className="leaderboard-flag" />
              <span className="player-name">{entry.player_name}</span>
              <span className="player-stat">{formatValue(entry.value, card)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="leaderboards-sections">
      {SECTIONS.map(section => {
        const cards = section.cards.map(renderCard).filter(Boolean);
        if (cards.length === 0) return null;
        return (
          <section key={section.title} className="leaderboard-section">
            <h2 className="leaderboard-section-title">{section.title}</h2>
            <div className="leaderboards-grid">{cards}</div>
          </section>
        );
      })}
    </div>
  );
}
